/**
 * rother3 companion — mounting a comparison page.
 *
 * One module serves all seven pages. Each HTML file is a thin shell that names its family and
 * loads this; the shell exists so every page has a real URL, a real `<title>`, and something to
 * link to, while the logic stays in one testable place rather than seven copies that drift.
 *
 * ## Why the family is in the shell and not the URL alone
 *
 * `compare-page.html?f=reviews` would be one file and seven addresses. Seven files is better:
 * the browser's back button works, each page is bookmarkable, and a mistyped URL cannot silently
 * show the wrong data family. The shared script reads which page it is on from
 * `document.body.dataset.family`.
 */
import { initI18n, t } from './i18n.js';
import { mountThemeToggle } from './theme.js';
import { getAllAudits, dedupeByPlace, anchor as anchorPref } from './storage.js';
import { auditFields } from './audit-shape.js';
import { buildFamily } from './compare-families.js';
import { escapeHtml, escapeAttribute } from './html.js';
import {
  buildColumns, buildBusinessRows, applyView, renderTable, renderPager, renderLeaders,
  sortableColumns, PAGE_SIZE,
} from './compare-page.js';
import {
  buildScrollIndicator, mountScrollIndicator, makeScrollableRegionFocusable,
} from './table-scroll.js';

const params = new URLSearchParams(window.location.search);

/** Which family this page shows, from its own `data-family`. */
const familyId = document.body.dataset.family ?? 'profile';

const els = {
  table: document.getElementById('fam-table'),
  pager: document.getElementById('fam-pager'),
  leaders: document.getElementById('fam-leaders'),
  filter: document.getElementById('fam-filter'),
  sort: document.getElementById('fam-sort'),
  status: document.getElementById('fam-status'),
  count: document.getElementById('fam-business-count'),
  sortDir: document.getElementById('fam-sort-dir'),
};

/** The view state the controls read and write. Kept in the URL so a page is shareable. */
const state = {
  filter: params.get('q') ?? '',
  sort: params.get('sort') ?? 'name',
  direction: params.get('dir') === 'desc' ? 'desc' : 'asc',
  page: Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1),
};

/**
 * Every field, deduplicated, anchor first.
 *
 * `dedupeByPlace` collapses repeat imports of the same business, and the anchor is spliced in
 * at index 0 because every row model in this project treats `cells[0]` as the anchor.
 */
let fields = [];
let columns = [];
let rows = [];

/** Teardown for the current scroll-indicator mount. See the call site in `render()`. */
let detachScroll = null;

/**
 * Put the anchor first, or leave it absent.
 *
 * The old workspace silently fell back to "no anchor", which rendered every row as a competitor
 * including the reader's own business. Here an absent anchor is **stated**: the page says no
 * business is marked as yours, and the user can mark one from any row.
 */
function orderFields(allFields) {
  const anchorPlaceId = anchorPref.get();
  const mine = anchorPlaceId ? allFields.find((f) => f.placeId === anchorPlaceId) : null;
  const others = allFields.filter((f) => f.placeId !== anchorPlaceId);
  return mine ? { ordered: [mine, ...others], anchored: true } : { ordered: others, anchored: false };
}

function showStatus(text, type = 'info') {
  if (!els.status) {return;}
  els.status.textContent = text;
  els.status.className = `status status-${type}`;
  els.status.hidden = false;
}

/** Keep the URL in step, without a history entry per keystroke. */
function syncUrl(replace = true) {
  const next = new URL(window.location.href);
  const set = (k, v, fallback) => {
    if (v === fallback || v === '') {next.searchParams.delete(k);} else {next.searchParams.set(k, v);}
  };
  set('q', state.filter, '');
  set('sort', state.sort, 'name');
  set('dir', state.direction, 'asc');
  set('page', String(state.page), '1');
  window.history[replace ? 'replaceState' : 'pushState']({}, '', next);
}

/**
 * Redraw everything from `state`.
 *
 * One function, called on load and on every control change, so there is exactly one place where
 * the table can disagree with the controls — and it is this one.
 */
function render() {
  const view = applyView(rows, {
    filter: state.filter,
    sort: state.sort,
    direction: state.direction,
    page: state.page,
    pageSize: PAGE_SIZE,
  });

  // A filter that removes the last page must not leave the reader on an empty page 4.
  if (view.page !== state.page) {
    state.page = view.page;
    return render();
  }

  /**
   * Storage said there are businesses and the table rendered none.
   *
   * That combination is a wiring fault, not an empty database — and it is exactly the failure
   * that shipped once already: every function called was individually correct, so nothing threw
   * and the page rendered "0 businesses" over a database holding 137.
   *
   * Checked only when there is **no filter and no anchor**, because a filtered view is
   * legitimately allowed to come back empty, and a page whose only anchor is pinned reports a
   * different total from the one in storage.
   */
  if (fields.length > 0 && view.total === 0 && !state.filter && fields[0]?.anchor) {
    console.error(
      `[rother3] ${familyId}: storage holds ${fields.length} businesses but the table rendered ` +
        'none. The field list was not seeded before the rebuild.'
    );
  }

  const family = buildFamily(familyId, fields);
  const sections = family ? family.sections : [];

  els.table.innerHTML = renderTable({ ...view, caption: family?.title }, columns);
  els.pager.innerHTML = renderPager(view);
  els.leaders.innerHTML = renderLeaders(rows, columns);

  /**
   * Mount the scroll indicator on **every** render.
   *
   * `renderTable` replaced the wrapper element, so the previous mount's listeners point at a
   * detached node and the indicator stops tracking the moment the reader filters or pages. The
   * teardown is threaded through `detachScroll` so the old listeners go rather than accumulating
   * one set per keystroke — which is what happens if the module simply re-binds.
   */
  const host = els.table.querySelector('.fam-scroll-host');
  const wrapper = els.table.querySelector('.table-wrapper');
  if (host && wrapper) {
    host.replaceChildren(
      buildScrollIndicator({
        leftLabel: t('fam_scroll_left', 'Scroll the table left'),
        rightLabel: t('fam_scroll_right', 'Scroll the table right'),
      })
    );
    makeScrollableRegionFocusable(
      wrapper,
      t('fam_scroll_region', 'Comparison table, scrollable')
    );
    detachScroll = mountScrollIndicator(wrapper, host.firstElementChild, detachScroll);
  }

  if (els.filter && els.filter.value !== state.filter) {els.filter.value = state.filter;}
  if (els.sort && els.sort.value !== state.sort) {els.sort.value = state.sort;}

  if (els.sortDir) {
    els.sortDir.textContent = state.direction === 'desc' ? '↓' : '↑';
    els.sortDir.setAttribute(
      'aria-label',
      state.direction === 'desc' ? 'Sorted high to low' : 'Sorted low to high'
    );
  }

  if (els.count) {
    els.count.textContent = `${view.total} ${view.total === 1 ? 'business' : 'businesses'}`;
  }

  syncUrl();
  return view;
}

/** Rebuild `columns` and `rows` from storage, then redraw. */
function rebuild() {
  const view = orderFields(fields);
  fields = view.ordered;

  const family = buildFamily(familyId, fields);
  columns = family ? buildColumns(family.sections) : [];
  rows = buildBusinessRows(fields, columns);

  els.table.hidden = family === null;
  return render();
}

/** Populate the sort control with the columns this family can actually sort by. */
function mountSortOptions() {
  if (!els.sort) {return;}
  const sortable = sortableColumns(columns)
    .map((c) => `<option value="${escapeAttribute(c.key)}">${escapeHtml(c.label)}</option>`)
    .join('');

  els.sort.innerHTML = `<option value="name">Name</option>${sortable}`;
  els.sort.value = state.sort;
}

/** Typing filters immediately; 200 ms so a fast typist does not trigger 8 renders. */
function debounce(fn, ms) {
  let handle = null;
  return (...args) => {
    clearTimeout(handle);
    handle = setTimeout(() => fn(...args), ms);
  };
}

async function init() {
  mountThemeToggle();
  await initI18n();

  const family = buildFamily(familyId, []);
  if (family === null) {
    showStatus(t('fam_unknown', 'That comparison page does not exist.'), 'error');
    return;
  }

  let audits = [];
  try {
    audits = await getAllAudits();
  } catch (err) {
    showStatus(t('fam_storage_error', 'This browser would not open its local database.'), 'error');
    return;
  }

const { fields: unique } = dedupeByPlace(audits);

  if (unique.length === 0) {
    els.status.hidden = false;
    els.status.className = 'status status-info';
    els.status.textContent = t('fam_no_data', 'No businesses are imported yet.');
    els.table.innerHTML = `<p class="fam-empty">
      <a href="index.html">${escapeHtml(t('fam_import_first', 'Import an audit to compare businesses'))}</a>
    </p>`;
    return;
  }

  /**
   * Seed `fields` before rebuilding.
   *
   * `rebuild()` orders the module-level `fields`, which starts empty. Passing `unique` only to
   * the emptiness check and never into it meant the page loaded, found storage, found 137
   * businesses, and then rendered a table of **zero rows** — with no error, because every call
   * was individually valid. It rendered "0 businesses" over a database holding 137.
   *
   * Nothing in the unit tests caught it: they test `buildFamily`, `buildColumns` and
   * `buildBusinessRows` as separate functions, and each is correct. The bug is in the wiring
   * between them, which is exactly what the end-to-end pass exists to exercise.
   */
  const ordered = orderFields(unique);
  fields = ordered.ordered;

  rebuild();
  mountSortOptions();

  els.filter?.addEventListener('input', debounce((e) => {
    state.filter = e.target.value;
    state.page = 1;
    render();
  }, 200));

  els.sort?.addEventListener('change', (e) => {
    state.sort = e.target.value;
    state.page = 1;
    render();
  });

  els.sortDir?.addEventListener('click', () => {
    state.direction = state.direction === 'desc' ? 'asc' : 'desc';
    render();
  });

  els.pager?.addEventListener('click', (e) => {
    const button = e.target.closest('[data-page]');
    if (!button || button.disabled) {return;}
    state.page = parseInt(button.dataset.page, 10);
    render();
    els.table.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  /**
   * Mark a row as "my business".
   *
   * Delegated on the table rather than bound per button, because the table is re-rendered on
   * every filter keystroke and a fresh render would drop listeners attached to buttons that no
   * longer exist.
   */
  els.table?.addEventListener('click', (e) => {
    const button = e.target.closest('[data-anchor-place]');
    if (!button) {return;}
    const placeId = button.dataset.anchorPlace;
    if (!placeId) {return;}
    anchorPref.set(placeId);
    showStatus(t('fam_anchor_set', 'Saved. This is your business from now on.'), 'success');
    window.location.reload();
  });

  window.addEventListener('popstate', () => {
    const next = new URLSearchParams(window.location.search);
    state.filter = next.get('q') ?? '';
    state.sort = next.get('sort') ?? 'name';
    state.direction = next.get('dir') === 'desc' ? 'desc' : 'asc';
    state.page = Math.max(1, parseInt(next.get('page') ?? '1', 10) || 1);
    render();
  });
}


init();